import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WorkflowPractice } from "@/components/WorkflowPractice";
import {
  categories,
  library,
  GENRE_LABEL,
  type LibraryEntry,
} from "@/data/aiWorkflows";
import { START_HERE_WORKFLOWS } from "@/data/promptLibraryStartHere";
import {
  browseWorkflows,
  WORKFLOWS_PER_PAGE,
} from "@/utils/promptLibrarySearch";
import { LIBRARY_PATH, workflowPath } from "@/utils/promptLibraryShare";
import { trackEvent } from "@/utils/posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { promptLibraryCampaignQuery } from "@/utils/promptLibraryConversion";
import { PromptLibraryAI30Offer } from "@/components/PromptLibraryAI30";

export const AIWorkflowLibrary = ({
  selectedWorkflow,
}: {
  selectedWorkflow?: LibraryEntry;
}) => {
  const location = useLocation();
  const campaignQuery = promptLibraryCampaignQuery(useHydratedSearch());
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(WORKFLOWS_PER_PAGE);
  const { matches, visible } = useMemo(
    () => browseWorkflows(library, searchQuery, activeCategory, visibleCount),
    [searchQuery, activeCategory, visibleCount],
  );
  const isFiltered = searchQuery.trim().length > 0 || activeCategory !== "all";
  const resetFilters = () => {
    setSearchQuery("");
    setActiveCategory("all");
    setVisibleCount(WORKFLOWS_PER_PAGE);
  };

  // Old shared anchors now open the same dedicated page as every card link.
  // Keep attribution on that internal navigation.
  useEffect(() => {
    const hash = location.hash.slice(1);
    const entry = library.find(
      (item) =>
        hash === `${item.category.id}-${item.number}` || hash === item.slug,
    );
    if (entry && !selectedWorkflow) {
      navigate(
        { pathname: workflowPath(entry.slug), search: location.search },
        { replace: true },
      );
      return;
    }
    if (!hash) return;
    const id = ["level-1", "the-path", "level-2", "how-this-works"].includes(
      hash,
    )
      ? "workflow-results"
      : hash;
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(id);
      const details = target?.closest("details");
      if (details) details.open = true;
      target?.scrollIntoView({ block: "start" });
      if (id === "workflow-search") target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash, location.search, navigate, selectedWorkflow]);

  if (selectedWorkflow) {
    const id = `${selectedWorkflow.category.id}-${selectedWorkflow.number}`;
    return (
      <section
        id="workflow-library"
        className="bg-longhand-paper px-4 py-6 text-longhand-ink sm:px-6"
      >
        <div className="mx-auto max-w-5xl">
          <a
            href={`${LIBRARY_PATH}${campaignQuery}#workflow-search`}
            className="mb-5 inline-flex min-h-11 items-center font-semibold text-longhand-accent underline"
          >
            Explore all {library.length} workflows
          </a>
          <article
            id={id}
            className="min-w-0 scroll-mt-24 overflow-hidden rounded-lg border border-longhand-border"
          >
            <header className="p-5 sm:p-6">
              <p className="text-xs font-semibold text-longhand-accent">
                {GENRE_LABEL[selectedWorkflow.category.id]}
              </p>
              <h2 className="mt-2 font-serif text-2xl font-semibold">
                Put it to work
              </h2>
            </header>
            <div id={`${id}-workflow-details`}>
              <WorkflowPractice entry={selectedWorkflow} standalone />
            </div>
          </article>
        </div>
      </section>
    );
  }

  return (
    <div className="bg-longhand-paper text-longhand-ink">
      <section
        id="workflow-library"
        className="scroll-mt-24 px-4 pb-10 pt-6 sm:px-6 sm:pt-8 lg:px-8"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="sr-only">Find a workflow</h2>
          <div className="grid items-end gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
            <div>
              <label
                htmlFor="workflow-search"
                className="text-sm font-semibold"
              >
                Search workflows
              </label>
              <div className="relative mt-2">
                <Search
                  className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-longhand-ink/70"
                  aria-hidden="true"
                />
                <input
                  id="workflow-search"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setVisibleCount(WORKFLOWS_PER_PAGE);
                  }}
                  aria-controls="workflow-results"
                  aria-describedby="workflow-search-help"
                  placeholder="Try meeting notes, onboarding, or budget"
                  className="min-h-12 w-full scroll-mt-28 rounded-lg border border-longhand-border bg-longhand-paper py-3 pl-12 pr-4 text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-longhand-accent"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="workflow-category"
                className="text-sm font-semibold"
              >
                Team
              </label>
              <select
                id="workflow-category"
                value={activeCategory}
                onChange={(event) => {
                  setActiveCategory(event.target.value);
                  setVisibleCount(WORKFLOWS_PER_PAGE);
                  trackEvent("AI Top 40 Category Filtered", {
                    category: event.target.value,
                    source_page: "fortune-100-prompt-library",
                  });
                }}
                aria-controls="workflow-results"
                className="mt-2 block min-h-12 w-full max-w-full rounded-lg border border-longhand-border bg-longhand-paper p-3 text-base"
              >
                <option value="all">All teams</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {GENRE_LABEL[category.id]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p
            id="workflow-search-help"
            className="mt-2 text-sm text-longhand-ink/80"
          >
            Search by task, team, or the notes and files you have.
          </p>

          {!isFiltered && (
            <section
              id="start-here"
              className="my-7 scroll-mt-24"
              aria-labelledby="starter-title"
            >
              <h2
                id="starter-title"
                className="font-serif text-2xl font-semibold"
              >
                Pick a task to try.
              </h2>
              <ul className="mt-3 grid gap-x-8 sm:grid-cols-2">
                {START_HERE_WORKFLOWS.map((starter) => (
                  <li
                    key={starter.slug}
                    className="border-b border-longhand-border"
                  >
                    <a
                      href={`${workflowPath(starter.slug)}${campaignQuery}`}
                      className="group flex min-h-11 items-center justify-between gap-4 py-3 font-semibold text-longhand-ink hover:text-longhand-accent"
                    >
                      <span>{starter.job}</span>
                      <ArrowRight
                        className="h-4 w-4 shrink-0"
                        aria-hidden="true"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <div className="my-5 flex flex-wrap items-center justify-between gap-3">
            <p
              id="workflow-count"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="text-sm"
            >
              Showing {visible.length} of {matches.length} workflows
            </p>
            {isFiltered && (
              <button
                type="button"
                onClick={resetFilters}
                className="min-h-11 text-sm font-semibold text-longhand-accent underline"
              >
                Clear search and filters
              </button>
            )}
          </div>
          <div id="workflow-results" className="min-w-0 scroll-mt-24">
            {visible.length ? (
              <ul className="space-y-3">
                {visible.map((entry) => (
                  <li
                    key={entry.slug}
                    id={`${entry.category.id}-${entry.number}`}
                    className="scroll-mt-24 list-none"
                  >
                    <article
                      data-workflow-card={entry.slug}
                      className="flex min-w-0 flex-wrap items-center justify-between gap-4 rounded-lg border border-longhand-border p-5 sm:p-6"
                    >
                      <div className="min-w-0 flex-1 basis-64">
                        <p className="text-xs font-semibold text-longhand-accent">
                          {GENRE_LABEL[entry.category.id]}
                        </p>
                        <h3 className="mt-2 font-serif text-xl font-semibold sm:text-2xl">
                          <a
                            href={`${workflowPath(entry.slug)}${campaignQuery}`}
                            className="hover:text-longhand-accent hover:underline"
                          >
                            {entry.title}
                          </a>
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-longhand-ink/80">
                          {entry.purpose}
                        </p>
                      </div>
                      <Button
                        asChild
                        variant="outline"
                        className="min-h-11 border-longhand-border bg-longhand-paper text-longhand-ink hover:bg-longhand-accent-light"
                      >
                        <a
                          href={`${workflowPath(entry.slug)}${campaignQuery}`}
                          aria-label={`Try ${entry.title}`}
                        >
                          Try this task{" "}
                          <ArrowRight
                            className="ml-2 h-4 w-4"
                            aria-hidden="true"
                          />
                        </a>
                      </Button>
                    </article>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="border-t border-longhand-border py-8">
                <h3 className="font-serif text-2xl font-semibold">
                  No matches yet.
                </h3>
                <p className="mt-2 text-sm">
                  Try a simpler search, like meeting or onboarding, or choose
                  All teams.
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-3 min-h-11 font-semibold text-longhand-accent underline"
                >
                  Show all workflows
                </button>
              </div>
            )}
          </div>
          {visible.length < matches.length && (
            <div className="mt-6">
              <Button
                type="button"
                variant="outline"
                aria-controls="workflow-results"
                aria-describedby="workflow-count"
                className="min-h-11 border-longhand-border bg-longhand-paper text-longhand-ink hover:bg-longhand-accent-light"
                onClick={() => {
                  const nextEntry = matches[visible.length];
                  setVisibleCount((count) => count + WORKFLOWS_PER_PAGE);
                  requestAnimationFrame(() =>
                    document
                      .getElementById(
                        `${nextEntry.category.id}-${nextEntry.number}`,
                      )
                      ?.querySelector("a")
                      ?.focus({ preventScroll: true }),
                  );
                }}
              >
                Show more
              </Button>
            </div>
          )}
          <noscript>
            <p className="mt-6">Browse the remaining workflows below.</p>
            <ul>
              {library.slice(WORKFLOWS_PER_PAGE).map((entry) => (
                <li
                  key={entry.slug}
                  id={`${entry.category.id}-${entry.number}`}
                >
                  <a href={workflowPath(entry.slug)}>{entry.title}</a>
                </li>
              ))}
            </ul>
          </noscript>
        </div>
      </section>

      <PromptLibraryAI30Offer />
    </div>
  );
};
